import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import api from "@/api/api";
import { 
  logout as apiLogout, 
  getSubscriptionStatus,
  updateSubscriptionStatus as apiUpdateSubscriptionStatus
} from "@/api/auth";

export type SubscriptionStatus = 'trial' | 'paid' | 'free';

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  trialExpiresOn: Date | null;
  daysRemaining: number;
  hasSeenTrialNotice: boolean;
}

// Interface to track subscription with timestamp
interface CachedSubscription {
  data: SubscriptionInfo;
  timestamp: number;
  isLoading: boolean;
}

type AuthContextType = {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  login: () => void;
  logout: () => void;
  subscription: SubscriptionInfo | null;
  isSubscriptionLoading: boolean;
  lastSubscriptionChangeTime: number;
  updateSubscriptionStatus: (status: SubscriptionStatus) => Promise<void>;
  checkSubscriptionStatus: (forceRefresh?: boolean) => Promise<SubscriptionInfo>;
  checkAuthStatus: () => Promise<void>; // Add auth check function to the context type
};

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  
  // Replace subscription state with cached version
  const [subscriptionCache, setSubscriptionCache] = useState<CachedSubscription | null>(null);
  
  // Track the last time subscription was changed
  const [lastSubscriptionChangeTime, setLastSubscriptionChangeTime] = useState(0);
  
  // Define a cache threshold (30 minutes = 1800000ms)
  const CACHE_THRESHOLD = 1800000;
  
  // Getter for subscription that doesn't expose cache implementation
  const subscription = subscriptionCache?.data || null;
  const isSubscriptionLoading = subscriptionCache?.isLoading || false;

  // Track if we should check auth status again
  const [lastCheckTime, setLastCheckTime] = useState(0);

  // Function to check auth status - exposed to be called when needed
  const checkAuthStatus = async () => {
    // Don't check too frequently (prevent throttling)
    const now = Date.now();
    if (now - lastCheckTime < 1000) {
      return;
    }
    
    setLastCheckTime(now);
    setIsAuthLoading(true);
    
    try {
      // Make a request to the auth status endpoint
      await api.get('/auth/status');
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setIsAuthLoading(false);
    }
  };

  // Check authentication status only on initial mount - no periodic checks
  useEffect(() => {
    // Skip auth check on public pages when initial load
    const isPublicPage = window.location.pathname === "/" || 
                        window.location.pathname.startsWith('/login') || 
                        window.location.pathname.startsWith('/verify');
    
    // On public pages, only check if we think we might be authenticated
    if (isPublicPage && !isAuthenticated) {
      setIsAuthLoading(false);
    } else {
      // Only check once on mount
      checkAuthStatus();
    }
    
    // No interval setup - we've removed periodic checks
  }, []); // Empty dependency array means this only runs once on mount

  // Function to log in the user
  const login = () => {
    setIsAuthenticated(true);
  };

  // Function to log out the user
  const logout = async () => {
    try {
      await apiLogout();
      setIsAuthenticated(false);
      setSubscriptionCache(null);
    } catch (error) {
      console.error('Logout error:', error);
      setIsAuthenticated(false);
      setSubscriptionCache(null);
    }
  };

  // Rate limiting for subscription checks
  const [lastAttemptTime, setLastAttemptTime] = useState(0);
  const MIN_REQUEST_INTERVAL = 5000; // 5 seconds between attempts
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const MAX_CONSECUTIVE_FAILURES = 3;
  
  // Function to check and update subscription status, with caching and rate limiting
  const checkSubscriptionStatus = async (forceRefresh = false): Promise<SubscriptionInfo> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }

    const now = Date.now();
    
    // Use cache if available and not expired and not forced refresh
    if (
      !forceRefresh && 
      subscriptionCache && 
      now - subscriptionCache.timestamp < CACHE_THRESHOLD
    ) {
      return subscriptionCache.data;
    }
    
    // Rate limiting check - prevent frequent calls
    if (now - lastAttemptTime < MIN_REQUEST_INTERVAL) {
      console.log('Rate limiting subscription check - too frequent');
      if (subscriptionCache?.data) {
        return subscriptionCache.data;
      }
      throw new Error('Please wait before checking subscription status again');
    }
    
    // Circuit breaker - if we've had too many consecutive failures, use cache or throw
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      console.log('Circuit breaker active - too many subscription check failures');
      if (subscriptionCache?.data) {
        return subscriptionCache.data;
      }
      throw new Error('Subscription service currently unavailable');
    }
    
    // Update last attempt time for rate limiting
    setLastAttemptTime(now);
    
    // Set loading state
    setSubscriptionCache(prev => prev ? {
      ...prev,
      isLoading: true
    } : { data: null as any, timestamp: 0, isLoading: true });
    
    try {
      const subscriptionData = await getSubscriptionStatus();
      
      // Convert trialExpiresOn string to Date object if it exists
      if (subscriptionData.trialExpiresOn) {
        subscriptionData.trialExpiresOn = new Date(subscriptionData.trialExpiresOn);
      }
      
      // Update cache with new data and timestamp
      setSubscriptionCache({
        data: subscriptionData,
        timestamp: now,
        isLoading: false
      });
      
      // Reset consecutive failures counter on success
      setConsecutiveFailures(0);
      
      return subscriptionData;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      
      // Increment consecutive failures counter
      setConsecutiveFailures(prev => prev + 1);
      
      // Reset loading state on error
      setSubscriptionCache(prev => prev ? {
        ...prev,
        isLoading: false
      } : null);
      
      throw error;
    }
  };

  // Function to update subscription status
  const updateSubscriptionStatus = async (status: SubscriptionStatus): Promise<void> => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }

    // Set loading state
    setSubscriptionCache(prev => prev ? {
      ...prev,
      isLoading: true
    } : { data: null as any, timestamp: 0, isLoading: true });
    
    try {
      console.log(`Updating subscription status to ${status}...`);
      await apiUpdateSubscriptionStatus(status);
      
      // Refresh subscription data after update
      await checkSubscriptionStatus(true); // Force refresh
      
      // Update the last subscription change time
      setLastSubscriptionChangeTime(Date.now());
    } catch (error) {
      console.error('Error updating subscription status:', error);
      
      // Reset loading state on error
      setSubscriptionCache(prev => prev ? {
        ...prev,
        isLoading: false
      } : null);
      
      throw error;
    }
  };

  // Minimal check - only load once on auth and only if we have no data
  // We set a flag to prevent this from running more than once per session
  const [hasTriedInitialSubscriptionLoad, setHasTriedInitialSubscriptionLoad] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated && !isAuthLoading && subscriptionCache === null && !hasTriedInitialSubscriptionLoad) {
      setHasTriedInitialSubscriptionLoad(true);
      checkSubscriptionStatus().catch(error => {
        console.error('Failed to fetch initial subscription status:', error);
      });
    }
  }, [isAuthenticated, isAuthLoading, subscriptionCache, hasTriedInitialSubscriptionLoad]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isAuthLoading, 
      login, 
      logout,
      subscription,
      isSubscriptionLoading,
      lastSubscriptionChangeTime,
      updateSubscriptionStatus,
      checkSubscriptionStatus,
      checkAuthStatus // Expose the auth check function for event-based checking
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
